import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Button, Typography, Row, Col, Tabs, TimePicker, Space, message, Table, Tag, Modal, Progress, Tooltip, Statistic, Radio, Switch, Input, Alert } from 'antd';
import { ThunderboltOutlined, PlayCircleOutlined, DownloadOutlined, SettingOutlined, RobotOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined, EyeOutlined, ApiOutlined, CopyOutlined, GlobalOutlined, ThunderboltFilled } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import dayjs from 'dayjs';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// 添加动画样式
const pulsingMarkerStyle = `
.pulsing-marker {
    animation: pulse 1.5s infinite;
}

.completed-marker {
    animation: glow 2s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.2);
        opacity: 0.7;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes glow {
    0% {
        box-shadow: 0 0 5px #52c41a;
    }
    50% {
        box-shadow: 0 0 15px #52c41a, 0 0 25px #52c41a;
    }
    100% {
        box-shadow: 0 0 5px #52c41a;
    }
}
`;

// 动态添加样式到页面
if (!document.querySelector('#pulsing-marker-style')) {
    const style = document.createElement('style');
    style.id = 'pulsing-marker-style';
    style.textContent = pulsingMarkerStyle;
    document.head.appendChild(style);
}

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// 修复Leaflet默认图标问题
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface PredictionRecord {
    time: string;
    status: 'success' | 'warning' | 'error' | 'pending';
    lastUpdate?: string;
    accuracy?: number;
    predictionData?: any[];
}

interface OutputConfig {
    type: 'csv' | 'database' | 'api' | 'provide-api';
    enabled: boolean;
    frequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
    // CSV配置
    filePath?: string;
    fileFormat?: 'csv' | 'xlsx' | 'json';
    // 数据库配置
    dbType?: 'mysql' | 'postgresql' | 'oracle';
    connectionString?: string;
    tableName?: string;
    // API推送配置
    apiUrl?: string;
    httpMethod?: 'POST' | 'PUT' | 'PATCH';
    authToken?: string;
    // API提供配置
    apiToken?: string;
    allowedOrigins?: string[];
    rateLimit?: number;
}

interface AutoPredictionConfig {
    id: string;
    stationName: string;
    stationType: string;
    predictionTimes: string[];
    predictionTypes: ('short' | 'ultra-short')[];
    status: 'running' | 'stopped';
    createdTime: string;
    lastRunTime?: string;
    nextRunTime?: string;
    dailyPredictionRecords?: PredictionRecord[];
    outputConfig?: OutputConfig;
}

interface StationData {
    id: string;
    name: string;
    lat: number;
    lng: number;
    type: 'wind' | 'solar' | 'hydro';
    region: string;
    predictedStatus: 'pending' | 'predicting' | 'completed';
    predictionTime?: number;
    predictionData?: any[];
    accuracy?: number;
}

interface RegionData {
    name: string;
    code: string;
    center: [number, number];
    zoom: number;
    flag: string;
}

const PowerPrediction: React.FC = () => {
    const [activeTab, setActiveTab] = useState('batch');
    const [showResults, setShowResults] = useState(false);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState<AutoPredictionConfig | null>(null);
    const [configForm] = Form.useForm();
    const [isResultModalOpen, setIsResultModalOpen] = useState(false);
    const [selectedPredictionRecord, setSelectedPredictionRecord] = useState<PredictionRecord | null>(null);
    const [outputType, setOutputType] = useState('csv');
    const [isApiDemoModalOpen, setIsApiDemoModalOpen] = useState(false);
    const [selectedApiConfig, setSelectedApiConfig] = useState<AutoPredictionConfig | null>(null);
    // 批量预测相关状态
    const [batchRegion, setBatchRegion] = useState('china');
    const [batchPredictionType, setBatchPredictionType] = useState<'short' | 'ultra-short'>('short');
    const [batchStations, setBatchStations] = useState<StationData[]>([]);
    const [isBatchPredicting, setIsBatchPredicting] = useState(false);
    const [batchPredictionStats, setBatchPredictionStats] = useState({ completed: 0, total: 0, startTime: 0 });
    const [selectedStation, setSelectedStation] = useState<StationData | null>(null);
    const [isStationModalOpen, setIsStationModalOpen] = useState(false);

    // 地区配置
    const regions: RegionData[] = [
        { name: '中国南方', code: 'china', center: [23.5, 113.0], zoom: 6, flag: '🇨🇳' },
        { name: '越南', code: 'vietnam', center: [16.0, 106.0], zoom: 6, flag: '🇻🇳' },
        { name: '印度', code: 'india', center: [20.0, 77.0], zoom: 5, flag: '🇮🇳' },
        { name: '巴西', code: 'brazil', center: [-15.0, -47.0], zoom: 5, flag: '🇧🇷' },
    ];

    // 生成批量预测站点数据
    const generateBatchStations = (region: string): StationData[] => {
        const stationTypes: ('wind' | 'solar' | 'hydro')[] = ['wind', 'solar', 'hydro'];
        const stations: StationData[] = [];

        const regionConfigs = {
            china: {
                cities: [
                    { name: '深圳', lat: 22.5, lng: 114.1 }, { name: '广州', lat: 23.1, lng: 113.3 },
                    { name: '东莞', lat: 23.0, lng: 113.8 }, { name: '佛山', lat: 23.0, lng: 113.1 },
                    { name: '珠海', lat: 22.3, lng: 113.6 }, { name: '厦门', lat: 24.5, lng: 118.1 },
                    { name: '福州', lat: 26.1, lng: 119.3 }, { name: '南宁', lat: 22.8, lng: 108.3 },
                    { name: '昆明', lat: 25.0, lng: 102.7 }, { name: '海口', lat: 20.0, lng: 110.3 }
                ],
                stationCount: 3
            },
            vietnam: {
                cities: [
                    { name: '胡志明市', lat: 10.8, lng: 106.7 }, { name: '河内', lat: 21.0, lng: 105.8 },
                    { name: '岘港', lat: 16.1, lng: 108.2 }, { name: '平顺', lat: 10.9, lng: 108.1 },
                    { name: '宁顺', lat: 11.6, lng: 108.9 }, { name: '金瓯', lat: 9.2, lng: 105.1 },
                    { name: '同奈', lat: 10.9, lng: 107.2 }, { name: '广义', lat: 15.1, lng: 108.8 }
                ],
                stationCount: 2
            },
            india: {
                cities: [
                    { name: '拉贾斯坦', lat: 27.0, lng: 74.2 }, { name: '古吉拉特', lat: 23.0, lng: 72.6 },
                    { name: '马哈拉施特拉', lat: 19.8, lng: 75.3 }, { name: '卡纳塔克', lat: 15.3, lng: 75.7 },
                    { name: '安得拉邦', lat: 15.9, lng: 79.7 }, { name: '泰米尔纳德', lat: 11.1, lng: 78.7 }
                ],
                stationCount: 3
            },
            brazil: {
                cities: [
                    { name: '塞阿拉', lat: -4.5, lng: -37.8 }, { name: '米纳斯吉拉斯', lat: -18.1, lng: -44.3 },
                    { name: '巴伊亚', lat: -12.9, lng: -38.5 }, { name: '圣保罗', lat: -23.5, lng: -46.6 },
                    { name: '里约热内卢', lat: -22.9, lng: -43.2 }, { name: '南里奥格兰德', lat: -30.0, lng: -51.2 }
                ],
                stationCount: 2
            }
        };

        const config = regionConfigs[region as keyof typeof regionConfigs];
        if (!config) return [];

        config.cities.forEach((city, cityIndex) => {
            for (let i = 0; i < config.stationCount; i++) {
                const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                const station: StationData = {
                    id: `${region.toUpperCase()}_${cityIndex}_${i}`,
                    name: `${city.name}${type === 'wind' ? '风电' : type === 'solar' ? '光伏' : '水电'}场${i + 1}号`,
                    lat: city.lat + (Math.random() - 0.5) * 0.3,
                    lng: city.lng + (Math.random() - 0.5) * 0.3,
                    type,
                    region,
                    predictedStatus: 'pending',
                };
                stations.push(station);
            }
        });

        return stations;
    };

    // 生成模拟预测数据
    const generateMockPredictionData = () => {
        const data = [];
        for (let i = 0; i < 24; i++) {
            const time = String(i).padStart(2, '0') + ':00';
            const actual = 80 + Math.sin(i * Math.PI / 12) * 30 + Math.random() * 10;
            const predicted = actual + (Math.random() - 0.5) * 8;
            data.push({
                time,
                actual: Math.round(actual * 10) / 10,
                predicted: Math.round(predicted * 10) / 10
            });
        }
        return data;
    };

    const generateApiToken = () => {
        return 'pw_' + Math.random().toString(36).substr(2, 16) + Date.now().toString(36);
    };

    // 生成最近24小时的预测记录
    const generateRecentPredictionRecords = (predictionTimes: string[]) => {
        const now = new Date();
        const records: PredictionRecord[] = [];

        // 获取最近24小时内应该执行的预测时间点
        for (let hoursBack = 23; hoursBack >= 0; hoursBack--) {
            const targetTime = new Date(now.getTime() - hoursBack * 60 * 60 * 1000);
            const timeStr = `${String(targetTime.getHours()).padStart(2, '0')}:00`;

            // 检查这个时间点是否在配置的预测时间内
            if (predictionTimes.includes(timeStr)) {
                const isPending = hoursBack < 1; // 1小时内的为pending
                const hasWarning = Math.random() < 0.1; // 10%概率为warning

                let status: 'success' | 'warning' | 'error' | 'pending';
                if (isPending) {
                    status = 'pending';
                } else if (hasWarning) {
                    status = 'warning';
                } else {
                    status = 'success';
                }

                records.push({
                    time: timeStr,
                    status,
                    lastUpdate: isPending ? '' : `${targetTime.getFullYear()}-${String(targetTime.getMonth() + 1).padStart(2, '0')}-${String(targetTime.getDate()).padStart(2, '0')} ${String(targetTime.getHours()).padStart(2, '0')}:${String(targetTime.getMinutes() + Math.floor(Math.random() * 30)).padStart(2, '0')}`,
                    accuracy: isPending ? 0 : Math.round((90 + Math.random() * 8) * 10) / 10,
                    predictionData: isPending ? undefined : generateMockPredictionData()
                });
            }
        }

        return records;
    };

    // 自动预测配置列表
    const [autoConfigs, setAutoConfigs] = useState<AutoPredictionConfig[]>(() => {
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        return [
            {
                id: '1',
                stationName: '风电场A-1号机组',
                stationType: '风力发电',
                predictionTimes: ['02:00', '08:00', '14:00', '20:00'],
                predictionTypes: ['short', 'ultra-short'],
                status: 'running',
                createdTime: '2024-01-10 09:00',
                lastRunTime: `${currentDate} ${String(now.getHours() - 1).padStart(2, '0')}:00`,
                nextRunTime: now.getHours() < 20 ? `${currentDate} 20:00` : `${currentDate} 02:00`,
                dailyPredictionRecords: generateRecentPredictionRecords(['02:00', '08:00', '14:00', '20:00']),
                outputConfig: {
                    type: 'database',
                    enabled: true,
                    frequency: 'hourly',
                    dbType: 'mysql',
                    connectionString: 'mysql://localhost:3306/power_prediction',
                    tableName: 'wind_predictions'
                }
            },
            {
                id: '2',
                stationName: '光伏场B-1号方阵',
                stationType: '光伏发电',
                predictionTimes: ['08:00', '14:00'],
                predictionTypes: ['short'],
                status: 'stopped',
                createdTime: '2024-01-12 15:30',
                lastRunTime: `${currentDate} ${String(Math.max(8, now.getHours() - 1)).padStart(2, '0')}:00`,
                dailyPredictionRecords: generateRecentPredictionRecords(['08:00', '14:00']),
                outputConfig: {
                    type: 'provide-api',
                    enabled: true,
                    frequency: 'realtime',
                    apiToken: 'pw_solar_b1_8k2n9x4m7q1w',
                    allowedOrigins: ['https://monitor.solar-plant.com', 'https://api.energy-hub.cn'],
                    rateLimit: 500
                }
            }
        ];
    });

    // 手动预测状态
    const [manualSettings, setManualSettings] = useState({
        selectedDataset: '',
        predictionType: 'short'
    });

    // 站点列表（从场站管理获取）
    const stations = [
        { id: '1', name: '风电场A-1号机组', type: '风力发电' },
        { id: '2', name: '风电场A-2号机组', type: '风力发电' },
        { id: '3', name: '光伏场B-1号方阵', type: '光伏发电' },
        { id: '4', name: '水电站C-1号机组', type: '水力发电' },
    ];

    // D-1数据集列表（从历史数据管理获取）
    const d1Datasets = [
        {
            id: '1',
            name: '风电场A-1号机组 - D-1数据 (20240115)',
            station: '风电场A-1号机组',
            date: '2024-01-15',
            dataPoints: 288
        },
        {
            id: '2',
            name: '风电场A-2号机组 - D-1数据 (20240115)',
            station: '风电场A-2号机组',
            date: '2024-01-15',
            dataPoints: 288
        },
        {
            id: '3',
            name: '光伏场B-1号方阵 - D-1数据 (20240115)',
            station: '光伏场B-1号方阵',
            date: '2024-01-15',
            dataPoints: 144
        },
        {
            id: '4',
            name: '水电站C-1号机组 - D-1数据 (20240115)',
            station: '水电站C-1号机组',
            date: '2024-01-15',
            dataPoints: 288
        },
    ];

    // 预测时间点选项
    const predictionTimeOptions = [
        { value: '02:00', label: '02:00' },
        { value: '08:00', label: '08:00' },
        { value: '14:00', label: '14:00' },
        { value: '20:00', label: '20:00' },
    ];

    // 获取可用的站点（排除已配置的站点，编辑时排除当前站点外的其他站点）
    const getAvailableStations = () => {
        const configuredStations = autoConfigs.map(config => config.stationName);
        if (editingConfig) {
            // 编辑模式：只返回当前配置的站点
            return stations.filter(station => station.name === editingConfig.stationName);
        }
        // 新增模式：排除已配置的站点
        return stations.filter(station => !configuredStations.includes(station.name));
    };

    // 启动/停止自动预测
    const handleToggleAutoPredict = (configId: string) => {
        setAutoConfigs(prevConfigs =>
            prevConfigs.map(config => {
                if (config.id === configId) {
                    const newStatus = config.status === 'running' ? 'stopped' : 'running';
                    const now = new Date();
                    const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    return {
                        ...config,
                        status: newStatus,
                        nextRunTime: newStatus === 'running' ? (now.getHours() < 20 ? `${currentDate} 20:00` : `${currentDate} 02:00`) : undefined
                    };
                }
                return config;
            })
        );
        message.success('状态已更新');
    };

    // 删除配置
    const handleDeleteConfig = (configId: string) => {
        setAutoConfigs(prevConfigs => prevConfigs.filter(config => config.id !== configId));
        message.success('配置已删除');
    };

    // 编辑配置
    const handleEditConfig = (config: AutoPredictionConfig) => {
        setEditingConfig(config);
        setIsConfigModalOpen(true);
        setOutputType(config.outputConfig?.type || 'csv');
        configForm.setFieldsValue({
            stationName: config.stationName,
            predictionTimes: config.predictionTimes,
            predictionTypes: config.predictionTypes,
            // 输出配置
            outputType: config.outputConfig?.type || 'csv',
            outputEnabled: config.outputConfig?.enabled || false,
            outputFrequency: config.outputConfig?.frequency || 'daily',
            // CSV配置
            filePath: config.outputConfig?.filePath,
            fileFormat: config.outputConfig?.fileFormat,
            // 数据库配置
            dbType: config.outputConfig?.dbType,
            connectionString: config.outputConfig?.connectionString,
            tableName: config.outputConfig?.tableName,
            // API推送配置
            apiUrl: config.outputConfig?.apiUrl,
            httpMethod: config.outputConfig?.httpMethod,
            authToken: config.outputConfig?.authToken,
            // API提供配置
            apiToken: config.outputConfig?.apiToken,
            allowedOrigins: config.outputConfig?.allowedOrigins?.join(', '),
            rateLimit: config.outputConfig?.rateLimit,
        });
    };

    // 新增或编辑配置
    const handleSaveConfig = async () => {
        try {
            const values = await configForm.validateFields();

            // 构建输出配置
            const outputConfig: OutputConfig = {
                type: values.outputType,
                enabled: values.outputEnabled || false,
                frequency: values.outputFrequency,
                // 根据类型设置特定配置
                ...(values.outputType === 'csv' && {
                    filePath: values.filePath,
                    fileFormat: values.fileFormat
                }),
                ...(values.outputType === 'database' && {
                    dbType: values.dbType,
                    connectionString: values.connectionString,
                    tableName: values.tableName
                }),
                ...(values.outputType === 'api' && {
                    apiUrl: values.apiUrl,
                    httpMethod: values.httpMethod,
                    authToken: values.authToken
                }),
                ...(values.outputType === 'provide-api' && {
                    apiToken: values.apiToken || generateApiToken(),
                    allowedOrigins: values.allowedOrigins ? values.allowedOrigins.split(',').map((s: string) => s.trim()) : ['*'],
                    rateLimit: values.rateLimit || 1000
                })
            };

            if (editingConfig) {
                // 编辑模式
                setAutoConfigs(prevConfigs =>
                    prevConfigs.map(config =>
                        config.id === editingConfig.id
                            ? {
                                ...config,
                                predictionTimes: values.predictionTimes,
                                predictionTypes: values.predictionTypes,
                                outputConfig
                            }
                            : config
                    )
                );
                message.success('配置已更新');
            } else {
                // 新增模式
                const selectedStation = stations.find(s => s.name === values.stationName);
                const newConfig: AutoPredictionConfig = {
                    id: Date.now().toString(),
                    stationName: values.stationName,
                    stationType: selectedStation?.type || '',
                    predictionTimes: values.predictionTimes,
                    predictionTypes: values.predictionTypes,
                    status: 'stopped',
                    createdTime: new Date().toLocaleString('zh-CN'),
                    dailyPredictionRecords: generateRecentPredictionRecords(values.predictionTimes),
                    outputConfig
                };
                setAutoConfigs(prevConfigs => [...prevConfigs, newConfig]);
                message.success('配置已添加');
            }

            setIsConfigModalOpen(false);
            setEditingConfig(null);
            configForm.resetFields();
        } catch (error) {
            console.log('验证失败:', error);
        }
    };

    // 取消配置操作
    const handleCancelConfig = () => {
        setIsConfigModalOpen(false);
        setEditingConfig(null);
        configForm.resetFields();
    };

    // 预测状态相关函数（参考气象管理）
    const getPredictionIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'warning':
                return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
            case 'error':
                return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'pending':
                return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
            default:
                return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
        }
    };

    const getPredictionColor = (status: string) => {
        switch (status) {
            case 'success':
                return '#52c41a';
            case 'warning':
                return '#faad14';
            case 'error':
                return '#ff4d4f';
            case 'pending':
                return '#d9d9d9';
            default:
                return '#d9d9d9';
        }
    };

    const renderPredictionStatus = (records: PredictionRecord[]) => {
        return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {records.map((record, index) => (
                    <Tooltip
                        key={index}
                        title={
                            <div>
                                <div><strong>{record.time}</strong></div>
                                <div>状态: {record.status === 'success' ? '成功' : record.status === 'warning' ? '警告' : record.status === 'error' ? '失败' : '等待中'}</div>
                                {record.lastUpdate && <div>预测时间: {record.lastUpdate}</div>}
                                {record.accuracy && record.accuracy > 0 && <div>预测精度: {record.accuracy}%</div>}
                                {record.status !== 'pending' && <div style={{ color: '#1890ff', marginTop: '4px' }}>点击查看预测结果</div>}
                            </div>
                        }
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '4px',
                                border: `1px solid ${getPredictionColor(record.status)}`,
                                borderRadius: '4px',
                                backgroundColor: record.status === 'success' ? '#f6ffed' :
                                    record.status === 'warning' ? '#fffbe6' :
                                        record.status === 'error' ? '#fff2f0' : '#fafafa',
                                cursor: record.status !== 'pending' ? 'pointer' : 'default'
                            }}
                            onClick={() => {
                                if (record.status !== 'pending' && record.predictionData) {
                                    setSelectedPredictionRecord(record);
                                    setIsResultModalOpen(true);
                                }
                            }}
                        >
                            {getPredictionIcon(record.status)}
                            <Text style={{ fontSize: '10px', marginTop: '2px' }}>{record.time}</Text>
                        </div>
                    </Tooltip>
                ))}
            </div>
        );
    };

    const calculatePredictionProgress = (records: PredictionRecord[]) => {
        const completed = records.filter(r => r.status === 'success').length;
        return Math.round((completed / records.length) * 100);
    };

    const handleManualPredict = () => {
        if (!manualSettings.selectedDataset) {
            message.error('请选择D-1数据集');
            return;
        }
        setShowResults(true);
        message.success('手动预测已开始');
    };

    // 初始化批量预测站点
    useEffect(() => {
        setBatchStations(generateBatchStations(batchRegion));
    }, [batchRegion]);

    // 开始批量预测
    const handleStartBatchPrediction = () => {
        if (batchStations.length === 0) {
            message.error('当前地区没有可预测的站点');
            return;
        }

        setIsBatchPredicting(true);
        setBatchPredictionStats({ completed: 0, total: batchStations.length, startTime: Date.now() });

        // 重置所有站点状态
        const resetStations = batchStations.map(station => ({
            ...station,
            predictedStatus: 'pending' as const,
            predictionTime: undefined,
            predictionData: undefined,
            accuracy: undefined
        }));
        setBatchStations(resetStations);

        message.info(`开始批量预测 ${resetStations.length} 个站点...`, 3);

        // 开始逐个预测（突出大模型的快速优势）
        let currentIndex = 0;

        const predictNextStation = async () => {
            if (currentIndex >= resetStations.length) {
                setIsBatchPredicting(false);
                const totalTime = (Date.now() - Date.now()) / 1000;
                message.success(`🎉 批量预测完成！共预测 ${resetStations.length} 个站点，大模型平均用时仅 0.8 秒/站点`, 5);
                return;
            }

            const currentStation = resetStations[currentIndex];

            // 更新当前站点为预测中
            setBatchStations(prev => prev.map((station, index) =>
                index === currentIndex
                    ? { ...station, predictedStatus: 'predicting' as const, predictionTime: Date.now() }
                    : station
            ));

            // 模拟大模型快速预测（0.5-1.5秒）
            const predictionDelay = 500 + Math.random() * 1000;

            setTimeout(() => {
                const predictionData = generateMockPredictionData();
                const accuracy = 88 + Math.random() * 8; // 88-96%的精度
                const roundedAccuracy = Math.round(accuracy * 10) / 10;

                // 更新站点为完成状态
                setBatchStations(prev => prev.map((station, index) =>
                    index === currentIndex
                        ? {
                            ...station,
                            predictedStatus: 'completed' as const,
                            predictionData,
                            accuracy: roundedAccuracy
                        }
                        : station
                ));

                // 更新统计
                setBatchPredictionStats(prev => ({
                    ...prev,
                    completed: prev.completed + 1
                }));

                // 显示预测完成的消息
                message.success(`✅ ${currentStation.name} 预测完成！精度: ${roundedAccuracy}%`, 2);

                currentIndex++;

                // 继续预测下一个站点
                if (currentIndex < resetStations.length) {
                    setTimeout(() => predictNextStation(), 200);
                } else {
                    // 所有预测完成
                    setTimeout(() => {
                        setIsBatchPredicting(false);
                        message.success(`🎉 批量预测全部完成！共预测 ${resetStations.length} 个站点`, 5);
                    }, 500);
                }
            }, predictionDelay);
        };

        // 延迟一点开始，让用户看到初始状态
        setTimeout(() => predictNextStation(), 500);
    };

    // 停止批量预测
    const handleStopBatchPrediction = () => {
        setIsBatchPredicting(false);
        // 将预测中的站点状态设为待预测
        setBatchStations(prev => prev.map(station =>
            station.predictedStatus === 'predicting'
                ? { ...station, predictedStatus: 'pending' as const, predictionTime: undefined }
                : station
        ));
        message.warning('⏹️ 批量预测已停止');
    };

    // 点击站点查看详情
    const handleStationClick = (station: StationData) => {
        if (station.predictedStatus === 'completed' && station.predictionData) {
            setSelectedStation(station);
            setIsStationModalOpen(true);
        } else if (station.predictedStatus === 'predicting') {
            message.info('预测正在进行中，请稍等...');
        } else {
            message.info('该站点还未开始预测');
        }
    };

    // 获取站点标记颜色和样式
    const getStationMarkerColor = (station: StationData) => {
        switch (station.predictedStatus) {
            case 'pending':
                return '#87CEEB';  // 天蓝色 - 待预测
            case 'predicting':
                return '#1890ff'; // 蓝色 - 预测中
            case 'completed':
                return '#52c41a'; // 鲜绿色 - 已完成
            default:
                return '#87CEEB';
        }
    };

    // 获取站点标记大小
    const getStationMarkerRadius = (station: StationData) => {
        switch (station.predictedStatus) {
            case 'pending':
                return 6;   // 小尺寸 - 待预测
            case 'predicting':
                return 12;  // 大尺寸 - 预测中
            case 'completed':
                return 8;   // 原始尺寸 - 已完成
            default:
                return 6;
        }
    };

    // 获取站点标记透明度
    const getStationMarkerOpacity = (station: StationData) => {
        switch (station.predictedStatus) {
            case 'pending':
                return 0.5;  // 较透明 - 待预测
            case 'predicting':
                return 1.0;  // 完全不透明 - 预测中
            case 'completed':
                return 1.0;  // 完全不透明 - 已完成，突出显示
            default:
                return 0.5;
        }
    };

    // 获取当前地区信息
    const currentRegion = regions.find(r => r.code === batchRegion) || regions[0];

    const mockPredictionData = [
        { time: '15:00', actual: 125, predicted: 128 },
        { time: '16:00', actual: 132, predicted: 130 },
        { time: '17:00', actual: 128, predicted: 125 },
        { time: '18:00', actual: 115, predicted: 118 },
        { time: '19:00', actual: 95, predicted: 98 },
        { time: '20:00', actual: 85, predicted: 87 },
    ];

    return (
        <div className="page-transition">
            <Card>
                <Title level={2}>
                    <ThunderboltOutlined style={{ marginRight: '8px' }} />
                    功率预测
                </Title>

                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={
                            <span>
                                <RobotOutlined />
                                自动预测
                            </span>
                        }
                        key="auto"
                    >
                        <Card
                            title="自动预测配置管理"
                            extra={
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsConfigModalOpen(true)}
                                    disabled={getAvailableStations().length === 0}
                                >
                                    新增配置
                                </Button>
                            }
                            size="small"
                        >
                            <Table
                                dataSource={autoConfigs}
                                rowKey="id"
                                pagination={false}
                                columns={[
                                    {
                                        title: '站点名称',
                                        dataIndex: 'stationName',
                                        key: 'stationName',
                                        render: (text: string, record: AutoPredictionConfig) => (
                                            <div>
                                                <div>{text}</div>
                                                <Tag color="blue">{record.stationType}</Tag>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: '预测时间',
                                        dataIndex: 'predictionTimes',
                                        key: 'predictionTimes',
                                        render: (times: string[]) => (
                                            <div>
                                                {times.map(time => (
                                                    <Tag key={time} color="green">{time}</Tag>
                                                ))}
                                            </div>
                                        ),
                                    },
                                    {
                                        title: '预测类型',
                                        dataIndex: 'predictionTypes',
                                        key: 'predictionTypes',
                                        render: (types: string[]) => (
                                            <div>
                                                {types.map(type => (
                                                    <Tag key={type} color={type === 'short' ? 'blue' : 'orange'}>
                                                        {type === 'short' ? '短期' : '超短期'}
                                                    </Tag>
                                                ))}
                                            </div>
                                        ),
                                    },
                                    {
                                        title: '运行状态',
                                        dataIndex: 'status',
                                        key: 'status',
                                        render: (status: string) => (
                                            <Tag color={status === 'running' ? 'green' : 'red'}>
                                                {status === 'running' ? '运行中' : '已停止'}
                                            </Tag>
                                        ),
                                    },
                                    {
                                        title: '上次运行',
                                        dataIndex: 'lastRunTime',
                                        key: 'lastRunTime',
                                        render: (time?: string) => time || '-',
                                    },
                                    {
                                        title: '下次运行',
                                        dataIndex: 'nextRunTime',
                                        key: 'nextRunTime',
                                        render: (time?: string) => time || '-',
                                    },
                                    {
                                        title: '最近24小时',
                                        key: 'dailyPredictionRecords',
                                        render: (_: any, record: AutoPredictionConfig) => (
                                            <div>
                                                {record.dailyPredictionRecords && renderPredictionStatus(record.dailyPredictionRecords)}
                                                <div style={{ marginTop: '4px' }}>
                                                    <Progress
                                                        percent={record.dailyPredictionRecords ? calculatePredictionProgress(record.dailyPredictionRecords) : 0}
                                                        size="small"
                                                        showInfo={false}
                                                        strokeColor={record.dailyPredictionRecords ? calculatePredictionProgress(record.dailyPredictionRecords) === 100 ? '#52c41a' : '#1890ff' : '#d9d9d9'}
                                                    />
                                                    <Text style={{ fontSize: '10px', color: '#666' }}>
                                                        {record.dailyPredictionRecords ? calculatePredictionProgress(record.dailyPredictionRecords) : 0}% 完成
                                                    </Text>
                                                </div>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: '输出配置',
                                        key: 'outputConfig',
                                        render: (_: any, record: AutoPredictionConfig) => (
                                            <div>
                                                {record.outputConfig ? (
                                                    <div>
                                                        <Tag color={record.outputConfig.type === 'csv' ? 'blue' :
                                                            record.outputConfig.type === 'database' ? 'green' :
                                                                record.outputConfig.type === 'api' ? 'orange' : 'purple'}>
                                                            {record.outputConfig.type === 'csv' ? 'CSV文件' :
                                                                record.outputConfig.type === 'database' ? '数据库' :
                                                                    record.outputConfig.type === 'api' ? 'API推送' : '提供API'}
                                                        </Tag>
                                                        <Tag color={record.outputConfig.enabled ? 'green' : 'red'}>
                                                            {record.outputConfig.enabled ? '启用' : '禁用'}
                                                        </Tag>
                                                        <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                                            频率: {record.outputConfig.frequency === 'realtime' ? '实时' :
                                                                record.outputConfig.frequency === 'hourly' ? '每小时' :
                                                                    record.outputConfig.frequency === 'daily' ? '每日' : '每周'}
                                                        </div>
                                                        {record.outputConfig.type === 'provide-api' && (
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                style={{ padding: 0, height: 'auto', fontSize: '10px' }}
                                                                onClick={() => {
                                                                    setSelectedApiConfig(record);
                                                                    setIsApiDemoModalOpen(true);
                                                                }}
                                                            >
                                                                查看API调用示例
                                                            </Button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Tag color="gray">未配置</Tag>
                                                )}
                                            </div>
                                        ),
                                    },
                                    {
                                        title: '创建时间',
                                        dataIndex: 'createdTime',
                                        key: 'createdTime',
                                    },
                                    {
                                        title: '操作',
                                        key: 'action',
                                        render: (_: any, record: AutoPredictionConfig) => (
                                            <Space size="small">
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<PlayCircleOutlined />}
                                                    onClick={() => handleToggleAutoPredict(record.id)}
                                                >
                                                    {record.status === 'running' ? '停止' : '启动'}
                                                </Button>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => handleEditConfig(record)}
                                                >
                                                    编辑
                                                </Button>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleDeleteConfig(record.id)}
                                                >
                                                    删除
                                                </Button>
                                            </Space>
                                        ),
                                    },
                                ]}
                            />
                        </Card>
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <SettingOutlined />
                                手动预测
                            </span>
                        }
                        key="manual"
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Card title="手动预测配置" size="small">
                                    <Form layout="vertical">
                                        <Form.Item label="选择D-1数据集">
                                            <Select
                                                value={manualSettings.selectedDataset}
                                                onChange={(value) => setManualSettings({ ...manualSettings, selectedDataset: value })}
                                                placeholder="请选择D-1数据集"
                                            >
                                                {d1Datasets.map(dataset => (
                                                    <Option key={dataset.id} value={dataset.name}>
                                                        {dataset.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>

                                        <Form.Item label="预测类型">
                                            <Select
                                                value={manualSettings.predictionType}
                                                onChange={(value) => setManualSettings({ ...manualSettings, predictionType: value })}
                                                placeholder="选择预测类型"
                                            >
                                                <Option value="short">短期预测 (24小时)</Option>
                                                <Option value="ultra-short">超短期预测 (4小时)</Option>
                                            </Select>
                                        </Form.Item>

                                        <Form.Item>
                                            <Button
                                                type="primary"
                                                icon={<PlayCircleOutlined />}
                                                onClick={handleManualPredict}
                                                block
                                                size="large"
                                            >
                                                开始手动预测
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </Card>
                            </Col>

                            <Col span={12}>
                                <Card title="预测参数" size="small">
                                    <div style={{ padding: '16px' }}>
                                        {manualSettings.selectedDataset ? (
                                            <>
                                                <p><strong>数据集:</strong> {manualSettings.selectedDataset}</p>
                                                <p><strong>数据点数:</strong> {d1Datasets.find(d => d.name === manualSettings.selectedDataset)?.dataPoints || 0}</p>
                                                <p><strong>预测类型:</strong> {manualSettings.predictionType === 'short' ? '短期预测 (24小时)' : '超短期预测 (4小时)'}</p>
                                                <p><strong>基础模型:</strong> 大源模型</p>
                                                <p><strong>预测精度:</strong> 94.2%</p>
                                                <p><strong>数据采样频率:</strong> 15分钟</p>
                                            </>
                                        ) : (
                                            <p style={{ color: '#999' }}>请先选择D-1数据集</p>
                                        )}
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <ThunderboltFilled style={{ color: '#52c41a' }} />
                                批量预测
                                <Tag color="green" style={{ marginLeft: '4px', fontSize: '10px' }}>大模型加速</Tag>
                            </span>
                        }
                        key="batch"
                    >
                        {/* 批量预测控制面板 */}
                        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                            <Col span={24}>
                                <Alert
                                    message="⚡ 大模型批量预测"
                                    description="采用先进的大语言模型技术，实现超快速批量预测。平均每个站点预测时间仅需0.8秒，相比传统方法提速20倍以上！"
                                    type="success"
                                    showIcon
                                    style={{ marginBottom: '16px' }}
                                />
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col span={8}>
                                <Card title="批量预测配置" size="small">
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <div>
                                            <Text strong>选择地区：</Text>
                                            <Select
                                                value={batchRegion}
                                                onChange={setBatchRegion}
                                                style={{ width: '100%', marginTop: '8px' }}
                                                placeholder="选择预测地区"
                                            >
                                                {regions.map(region => (
                                                    <Option key={region.code} value={region.code}>
                                                        <span style={{ marginRight: '6px', fontSize: '16px' }}>{region.flag}</span>
                                                        {region.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </div>

                                        <div>
                                            <Text strong>预测类型：</Text>
                                            <Select
                                                value={batchPredictionType}
                                                onChange={setBatchPredictionType}
                                                style={{ width: '100%', marginTop: '8px' }}
                                                placeholder="选择预测类型"
                                            >
                                                <Option value="short">短期预测 (24小时)</Option>
                                                <Option value="ultra-short">超短期预测 (4小时)</Option>
                                            </Select>
                                        </div>

                                        <div style={{ marginTop: '16px' }}>
                                            {!isBatchPredicting ? (
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <Button
                                                        type="primary"
                                                        icon={<ThunderboltOutlined />}
                                                        onClick={handleStartBatchPrediction}
                                                        block
                                                        size="large"
                                                        disabled={batchStations.length === 0}
                                                        style={{
                                                            background: 'linear-gradient(90deg, #52c41a 0%, #73d13d 100%)',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        🚀 开始批量预测
                                                    </Button>
                                                    {batchStations.some(s => s.predictedStatus === 'completed') && (
                                                        <Button
                                                            icon={<SettingOutlined />}
                                                            onClick={() => {
                                                                setBatchStations(prev => prev.map(station => ({
                                                                    ...station,
                                                                    predictedStatus: 'pending' as const,
                                                                    predictionTime: undefined,
                                                                    predictionData: undefined,
                                                                    accuracy: undefined
                                                                })));
                                                                setBatchPredictionStats({ completed: 0, total: batchStations.length, startTime: 0 });
                                                                message.info('已重置所有站点状态');
                                                            }}
                                                            block
                                                        >
                                                            🔄 重置站点状态
                                                        </Button>
                                                    )}
                                                </Space>
                                            ) : (
                                                <Button
                                                    danger
                                                    icon={<ClockCircleOutlined />}
                                                    onClick={handleStopBatchPrediction}
                                                    block
                                                    size="large"
                                                >
                                                    ⏹️ 停止预测
                                                </Button>
                                            )}
                                        </div>
                                    </Space>
                                </Card>
                            </Col>

                            <Col span={8}>
                                <Card title="预测统计" size="small">
                                    <Row gutter={[16, 16]}>
                                        <Col span={12}>
                                            <Statistic
                                                title="站点总数"
                                                value={batchStations.length}
                                                prefix={<GlobalOutlined />}
                                            />
                                        </Col>
                                        <Col span={12}>
                                            <Statistic
                                                title="已完成"
                                                value={batchPredictionStats.completed}
                                                suffix={`/ ${batchPredictionStats.total}`}
                                                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                            />
                                        </Col>
                                        <Col span={24}>
                                            <div style={{ marginTop: '12px' }}>
                                                <Text strong>预测进度：</Text>
                                                <Progress
                                                    percent={batchPredictionStats.total > 0 ? Math.round((batchPredictionStats.completed / batchPredictionStats.total) * 100) : 0}
                                                    status={isBatchPredicting ? 'active' : 'normal'}
                                                    strokeColor={{
                                                        '0%': '#108ee9',
                                                        '100%': '#52c41a',
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                        {isBatchPredicting && batchPredictionStats.startTime > 0 && (
                                            <Col span={24}>
                                                <Statistic
                                                    title="平均预测速度"
                                                    value={batchPredictionStats.completed > 0 ? (Date.now() - batchPredictionStats.startTime) / 1000 / batchPredictionStats.completed : 0}
                                                    precision={1}
                                                    suffix="秒/站点"
                                                    prefix={<RobotOutlined style={{ color: '#1890ff' }} />}
                                                />
                                            </Col>
                                        )}
                                        {!isBatchPredicting && batchPredictionStats.completed > 0 && (
                                            <Col span={24}>
                                                <div style={{
                                                    marginTop: '12px',
                                                    padding: '8px',
                                                    background: '#f6ffed',
                                                    borderRadius: '4px',
                                                    textAlign: 'center'
                                                }}>
                                                    <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                                        🎉 预测完成！点击绿色✅站点查看详情
                                                    </Text>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </Card>
                            </Col>

                            <Col span={8}>
                                <Card title="地区信息" size="small">
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                                            {currentRegion.flag}
                                        </div>
                                        <Title level={4} style={{ margin: '8px 0' }}>
                                            {currentRegion.name}
                                        </Title>
                                        <div style={{ marginTop: '16px' }}>
                                            <Tag color="blue">
                                                {batchPredictionType === 'short' ? '短期预测' : '超短期预测'}
                                            </Tag>
                                            <Tag color="green">大模型</Tag>
                                        </div>
                                        <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
                                            <div>📍 坐标: {currentRegion.center[0].toFixed(1)}°, {currentRegion.center[1].toFixed(1)}°</div>
                                            <div>🔍 缩放: {currentRegion.zoom}x</div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        {/* 地图展示 */}
                        <Card
                            title={
                                <Space>
                                    <GlobalOutlined />
                                    {currentRegion.name}地区站点分布
                                    <Tag style={{ backgroundColor: '#87CEEB', color: '#fff', border: 'none' }}>⏳ 待预测</Tag>
                                    <Tag style={{ backgroundColor: '#1890ff', color: '#fff', border: 'none' }}>🔄 预测中</Tag>
                                    <Tag style={{ backgroundColor: '#52c41a', color: '#fff', border: 'none' }}>✅ 已完成(可点击)</Tag>
                                </Space>
                            }
                            extra={
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    💡 提示: 点击绿色✅站点查看预测详情
                                </div>
                            }
                            style={{ marginTop: '16px' }}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <div style={{ height: '600px', position: 'relative' }}>
                                {batchStations.length === 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 1000,
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        padding: '20px',
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>🌍</div>
                                        <div>正在加载 {currentRegion.name} 地区站点...</div>
                                    </div>
                                )}
                                {isBatchPredicting && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        zIndex: 1000,
                                        background: 'linear-gradient(90deg, #1890ff 0%, #52c41a 100%)',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                    }}>
                                        🚀 大模型正在快速预测... {batchPredictionStats.completed}/{batchPredictionStats.total}
                                    </div>
                                )}
                                <MapContainer
                                    center={currentRegion.center}
                                    zoom={currentRegion.zoom}
                                    style={{ height: '100%', width: '100%' }}
                                    key={`${batchRegion}-batch`}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    />
                                    {batchStations.map(station => (
                                        <CircleMarker
                                            key={station.id}
                                            center={[station.lat, station.lng]}
                                            radius={getStationMarkerRadius(station)}
                                            fillColor={getStationMarkerColor(station)}
                                            color={
                                                station.predictedStatus === 'pending' ? '#4682B4' :  // 钢蓝色边框 - 待预测
                                                    station.predictedStatus === 'predicting' ? '#fff' :
                                                        '#2f6e2f'  // 深绿色边框 - 已完成
                                            }
                                            weight={station.predictedStatus === 'predicting' ? 3 : 2}
                                            opacity={1}
                                            fillOpacity={getStationMarkerOpacity(station)}
                                            className={
                                                station.predictedStatus === 'predicting' ? 'pulsing-marker' :
                                                    station.predictedStatus === 'completed' ? 'completed-marker' : ''
                                            }
                                            eventHandlers={{
                                                click: () => handleStationClick(station)
                                            }}
                                        >
                                            <Popup>
                                                <div style={{ minWidth: '220px' }}>
                                                    <h4>
                                                        {station.name}
                                                        {station.predictedStatus === 'pending' && (
                                                            <Tag style={{ backgroundColor: '#87CEEB', color: '#fff', border: 'none', marginLeft: '8px' }}>⏳ 待预测</Tag>
                                                        )}
                                                        {station.predictedStatus === 'predicting' && (
                                                            <Tag style={{ backgroundColor: '#1890ff', color: '#fff', border: 'none', marginLeft: '8px' }}>🔄 预测中</Tag>
                                                        )}
                                                        {station.predictedStatus === 'completed' && (
                                                            <Tag style={{ backgroundColor: '#52c41a', color: '#fff', border: 'none', marginLeft: '8px' }}>✅ 已完成</Tag>
                                                        )}
                                                    </h4>
                                                    <div style={{
                                                        padding: '8px',
                                                        background: station.predictedStatus === 'pending' ? '#f5f5f5' :
                                                            station.predictedStatus === 'predicting' ? '#e6f7ff' : '#f6ffed',
                                                        borderRadius: '4px',
                                                        marginBottom: '8px'
                                                    }}>
                                                        <p style={{ margin: '4px 0' }}><strong>类型:</strong> {
                                                            station.type === 'wind' ? '🌪️ 风电' :
                                                                station.type === 'solar' ? '☀️ 光伏' : '💧 水电'
                                                        }</p>
                                                        <p style={{ margin: '4px 0' }}><strong>状态:</strong>
                                                            <span style={{
                                                                color: station.predictedStatus === 'pending' ? '#4682B4' :
                                                                    station.predictedStatus === 'predicting' ? '#1890ff' : '#52c41a',
                                                                fontWeight: 'bold',
                                                                marginLeft: '4px'
                                                            }}>
                                                                {station.predictedStatus === 'pending' ? '⏳ 等待预测' :
                                                                    station.predictedStatus === 'predicting' ? '🔄 正在预测...' : '✅ 预测完成'}
                                                            </span>
                                                        </p>
                                                        {station.accuracy && (
                                                            <p style={{ margin: '4px 0' }}><strong>预测精度:</strong> <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{station.accuracy}%</span></p>
                                                        )}
                                                        {station.predictionTime && station.predictedStatus === 'completed' && (
                                                            <p style={{ margin: '4px 0' }}><strong>预测用时:</strong> <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                                                {((Date.now() - station.predictionTime) / 1000).toFixed(1)}秒
                                                            </span></p>
                                                        )}
                                                    </div>
                                                    {station.predictedStatus === 'completed' && (
                                                        <div style={{ textAlign: 'center', marginTop: '8px' }}>
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleStationClick(station);
                                                                }}
                                                                style={{
                                                                    background: 'linear-gradient(90deg, #52c41a 0%, #73d13d 100%)',
                                                                    border: 'none',
                                                                    boxShadow: '0 2px 4px rgba(82, 196, 26, 0.3)'
                                                                }}
                                                            >
                                                                🔍 查看预测详情
                                                            </Button>
                                                        </div>
                                                    )}
                                                    {station.predictedStatus === 'predicting' && (
                                                        <div style={{ textAlign: 'center', marginTop: '8px', color: '#1890ff' }}>
                                                            <div style={{ fontSize: '12px' }}>
                                                                🚀 大模型正在快速预测中...
                                                            </div>
                                                        </div>
                                                    )}
                                                    {station.predictedStatus === 'pending' && (
                                                        <div style={{ textAlign: 'center', marginTop: '8px', color: '#4682B4' }}>
                                                            <div style={{ fontSize: '12px' }}>
                                                                等待开始预测
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Popup>
                                        </CircleMarker>
                                    ))}
                                </MapContainer>
                            </div>
                        </Card>
                    </TabPane>
                </Tabs>

                {showResults && activeTab === 'manual' && (
                    <Card
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>手动预测结果图表</span>
                                <Button icon={<DownloadOutlined />}>导出结果</Button>
                            </div>
                        }
                        style={{ marginTop: '16px' }}
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={mockPredictionData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="time" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="actual"
                                    stroke="#8884d8"
                                    name="实际功率"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="predicted"
                                    stroke="#82ca9d"
                                    name="预测功率"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                )}

                {/* 新增/编辑自动预测配置模态框 */}
                <Modal
                    title={editingConfig ? "编辑自动预测配置" : "新增自动预测配置"}
                    open={isConfigModalOpen}
                    onOk={handleSaveConfig}
                    onCancel={handleCancelConfig}
                    width={700}
                >
                    <Form
                        form={configForm}
                        layout="vertical"
                    >
                        <Form.Item
                            name="stationName"
                            label="选择站点"
                            rules={[{ required: true, message: '请选择站点' }]}
                        >
                            <Select
                                placeholder="请选择要配置自动预测的站点"
                                disabled={!!editingConfig}
                            >
                                {getAvailableStations().map(station => (
                                    <Option key={station.id} value={station.name}>
                                        {station.name} ({station.type})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="predictionTimes"
                            label="预测时间点"
                            rules={[{ required: true, message: '请选择预测时间点' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="选择预测时间点（可多选）"
                            >
                                {predictionTimeOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="predictionTypes"
                            label="预测类型"
                            rules={[{ required: true, message: '请选择预测类型' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="选择预测类型（可多选）"
                            >
                                <Option value="short">短期预测 (24小时)</Option>
                                <Option value="ultra-short">超短期预测 (4小时)</Option>
                            </Select>
                        </Form.Item>

                        <Card title="输出配置" size="small" style={{ marginBottom: '16px' }}>
                            <Form.Item label="输出类型" name="outputType">
                                <Radio.Group value={outputType} onChange={(e) => setOutputType(e.target.value)}>
                                    <Radio value="csv">CSV文件</Radio>
                                    <Radio value="database">数据库</Radio>
                                    <Radio value="api">API推送</Radio>
                                    <Radio value="provide-api">提供API</Radio>
                                </Radio.Group>
                            </Form.Item>

                            {outputType === 'csv' && (
                                <>
                                    <Form.Item label="文件路径" name="filePath">
                                        <Input placeholder="例如: /exports/predictions/" />
                                    </Form.Item>
                                    <Form.Item label="文件格式" name="fileFormat">
                                        <Select placeholder="选择文件格式">
                                            <Option value="csv">CSV</Option>
                                            <Option value="xlsx">Excel</Option>
                                            <Option value="json">JSON</Option>
                                        </Select>
                                    </Form.Item>
                                </>
                            )}

                            {outputType === 'database' && (
                                <>
                                    <Form.Item label="数据库类型" name="dbType">
                                        <Select placeholder="选择数据库类型">
                                            <Option value="mysql">MySQL</Option>
                                            <Option value="postgresql">PostgreSQL</Option>
                                            <Option value="oracle">Oracle</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item label="连接字符串" name="connectionString">
                                        <Input placeholder="数据库连接字符串" />
                                    </Form.Item>
                                    <Form.Item label="表名" name="tableName">
                                        <Input placeholder="目标表名" />
                                    </Form.Item>
                                </>
                            )}

                            {outputType === 'api' && (
                                <>
                                    <Form.Item label="API地址" name="apiUrl">
                                        <Input placeholder="http://api.example.com/predictions" />
                                    </Form.Item>
                                    <Form.Item label="请求方法" name="httpMethod">
                                        <Select placeholder="选择HTTP方法">
                                            <Option value="POST">POST</Option>
                                            <Option value="PUT">PUT</Option>
                                            <Option value="PATCH">PATCH</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item label="认证Token" name="authToken">
                                        <Input.Password placeholder="API认证Token" />
                                    </Form.Item>
                                </>
                            )}

                            {outputType === 'provide-api' && (
                                <>
                                    <Form.Item label="API Token" name="apiToken">
                                        <Input.Search
                                            placeholder="自动生成的API Token"
                                            enterButton="重新生成"
                                            onSearch={() => {
                                                const newToken = generateApiToken();
                                                configForm.setFieldsValue({ apiToken: newToken });
                                            }}
                                        />
                                    </Form.Item>
                                    <Form.Item label="允许来源" name="allowedOrigins">
                                        <Input placeholder="多个来源用逗号分隔，* 表示允许所有来源" />
                                    </Form.Item>
                                    <Form.Item label="请求限制" name="rateLimit">
                                        <Input
                                            type="number"
                                            placeholder="每小时最大请求次数"
                                            addonAfter="次/小时"
                                        />
                                    </Form.Item>
                                    <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px', padding: '12px', margin: '16px 0' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>API 访问信息:</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            <p><strong>接口地址:</strong> http://api.power-predict.com/v1/predictions/:stationId</p>
                                            <p><strong>请求方法:</strong> GET</p>
                                            <p><strong>认证方式:</strong> Bearer Token (在Header中添加 Authorization: Bearer [token])</p>
                                        </div>
                                    </div>
                                </>
                            )}

                            <Form.Item label="输出频率" name="outputFrequency">
                                <Select placeholder="选择输出频率">
                                    <Option value="realtime">实时</Option>
                                    <Option value="hourly">每小时</Option>
                                    <Option value="daily">每日</Option>
                                    <Option value="weekly">每周</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="启用输出" name="outputEnabled" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Card>

                        <div style={{ color: '#666', fontSize: '12px' }}>
                            <p>注意：</p>
                            {editingConfig ? (
                                <>
                                    <p>• 编辑配置可以修改预测时间点、预测类型和输出配置</p>
                                    <p>• 站点信息不可修改</p>
                                    <p>• 修改后的配置立即生效</p>
                                </>
                            ) : (
                                <>
                                    <p>• 每个站点只能有一个自动预测配置</p>
                                    <p>• 可以选择多种预测类型：短期(24小时)和超短期(4小时)</p>
                                    <p>• 支持多种输出方式：CSV文件、数据库、API接口</p>
                                    <p>• 配置创建后默认为停止状态，需要手动启动</p>
                                    <p>• 预测将使用大源模型进行</p>
                                </>
                            )}
                        </div>
                    </Form>
                </Modal>

                {/* 预测结果查看模态框 */}
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <EyeOutlined />
                            预测结果详情
                            {selectedPredictionRecord && (
                                <Tag color="blue">
                                    {selectedPredictionRecord.time} 预测
                                </Tag>
                            )}
                        </div>
                    }
                    open={isResultModalOpen}
                    onCancel={() => {
                        setIsResultModalOpen(false);
                        setSelectedPredictionRecord(null);
                    }}
                    footer={[
                        <Button key="download" icon={<DownloadOutlined />}>
                            导出数据
                        </Button>,
                        <Button key="close" onClick={() => {
                            setIsResultModalOpen(false);
                            setSelectedPredictionRecord(null);
                        }}>
                            关闭
                        </Button>
                    ]}
                    width={900}
                >
                    {selectedPredictionRecord && (
                        <div>
                            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                                <Col span={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="预测精度"
                                            value={selectedPredictionRecord.accuracy}
                                            suffix="%"
                                            valueStyle={{ color: selectedPredictionRecord.accuracy && selectedPredictionRecord.accuracy > 90 ? '#52c41a' : '#faad14' }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="预测时间"
                                            value={selectedPredictionRecord.time}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="状态"
                                            value={selectedPredictionRecord.status === 'success' ? '成功' :
                                                selectedPredictionRecord.status === 'warning' ? '警告' : '失败'}
                                            valueStyle={{
                                                color: selectedPredictionRecord.status === 'success' ? '#52c41a' :
                                                    selectedPredictionRecord.status === 'warning' ? '#faad14' : '#ff4d4f'
                                            }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="更新时间"
                                            value={selectedPredictionRecord.lastUpdate?.split(' ')[1] || '-'}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Card title="24小时功率预测曲线" size="small">
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart data={selectedPredictionRecord.predictionData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="time" />
                                        <YAxis label={{ value: '功率 (MW)', angle: -90, position: 'insideLeft' }} />
                                        <RechartsTooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="actual"
                                            stroke="#8884d8"
                                            name="实际功率"
                                            strokeWidth={2}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="predicted"
                                            stroke="#82ca9d"
                                            name="预测功率"
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card>
                        </div>
                    )}
                </Modal>

                {/* API调用示例模态框 */}
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ApiOutlined />
                            API调用示例
                            {selectedApiConfig && (
                                <Tag color="purple">
                                    {selectedApiConfig.stationName}
                                </Tag>
                            )}
                        </div>
                    }
                    open={isApiDemoModalOpen}
                    onCancel={() => {
                        setIsApiDemoModalOpen(false);
                        setSelectedApiConfig(null);
                    }}
                    footer={[
                        <Button key="copy" type="primary" icon={<CopyOutlined />}>
                            复制示例代码
                        </Button>,
                        <Button key="close" onClick={() => {
                            setIsApiDemoModalOpen(false);
                            setSelectedApiConfig(null);
                        }}>
                            关闭
                        </Button>
                    ]}
                    width={800}
                >
                    {selectedApiConfig && selectedApiConfig.outputConfig && (
                        <div>
                            <Card title="API信息" size="small" style={{ marginBottom: '16px' }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <p><strong>接口地址:</strong></p>
                                        <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', fontFamily: 'monospace' }}>
                                            http://api.power-predict.com/v1/predictions/solar-b1
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <p><strong>API Token:</strong></p>
                                        <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', fontFamily: 'monospace' }}>
                                            {selectedApiConfig.outputConfig.apiToken}
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <p><strong>请求限制:</strong></p>
                                        <div>{selectedApiConfig.outputConfig.rateLimit} 次/小时</div>
                                    </Col>
                                    <Col span={12}>
                                        <p><strong>允许来源:</strong></p>
                                        <div>{selectedApiConfig.outputConfig.allowedOrigins?.join(', ') || '*'}</div>
                                    </Col>
                                </Row>
                            </Card>

                            <Tabs defaultActiveKey="curl">
                                <TabPane tab="cURL" key="curl">
                                    <div style={{ background: '#1f1f1f', color: '#ffffff', padding: '16px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '12px' }}>
                                        <pre style={{ margin: 0, color: '#ffffff' }}>{`# 获取最新预测数据
curl -X GET "http://api.power-predict.com/v1/predictions/solar-b1/latest" \\
  -H "Authorization: Bearer ${selectedApiConfig.outputConfig.apiToken}" \\
  -H "Content-Type: application/json"

# 获取指定时间范围的预测数据
curl -X GET "http://api.power-predict.com/v1/predictions/solar-b1?start=2024-01-15T08:00:00Z&end=2024-01-16T08:00:00Z" \\
  -H "Authorization: Bearer ${selectedApiConfig.outputConfig.apiToken}" \\
  -H "Content-Type: application/json"`}</pre>
                                    </div>
                                </TabPane>

                                <TabPane tab="JavaScript" key="javascript">
                                    <div style={{ background: '#1f1f1f', color: '#ffffff', padding: '16px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '12px' }}>
                                        <pre style={{ margin: 0, color: '#ffffff' }}>{`// 获取最新预测数据
const fetchLatestPrediction = async () => {
  try {
    const response = await fetch('http://api.power-predict.com/v1/predictions/solar-b1/latest', {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ${selectedApiConfig.outputConfig.apiToken}',
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('预测数据:', data);
      return data;
    } else {
      console.error('请求失败:', response.status);
    }
  } catch (error) {
    console.error('网络错误:', error);
  }
};

// 调用函数
fetchLatestPrediction();`}</pre>
                                    </div>
                                </TabPane>

                                <TabPane tab="Python" key="python">
                                    <div style={{ background: '#1f1f1f', color: '#ffffff', padding: '16px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '12px' }}>
                                        <pre style={{ margin: 0, color: '#ffffff' }}>{`import requests
import json

# API配置
API_BASE_URL = "http://api.power-predict.com/v1"
API_TOKEN = "${selectedApiConfig.outputConfig.apiToken}"
STATION_ID = "solar-b1"

# 请求头
headers = {
    "Authorization": f"Bearer {API_TOKEN}",
    "Content-Type": "application/json"
}

# 获取最新预测数据
def get_latest_prediction():
    url = f"{API_BASE_URL}/predictions/{STATION_ID}/latest"
    
    try:
        response = requests.get(url, headers=headers)
        
        if response.status_code == 200:
            data = response.json()
            print("预测数据:", json.dumps(data, indent=2, ensure_ascii=False))
            return data
        else:
            print(f"请求失败: {response.status_code}")
            return None
            
    except requests.exceptions.RequestException as e:
        print(f"网络错误: {e}")
        return None

# 调用函数
if __name__ == "__main__":
    prediction_data = get_latest_prediction()`}</pre>
                                    </div>
                                </TabPane>
                            </Tabs>

                            <Card title="返回数据格式" size="small" style={{ marginTop: '16px' }}>
                                <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '12px' }}>
                                    <pre style={{ margin: 0 }}>{`{
  "station_id": "solar-b1",
  "station_name": "光伏场B-1号方阵",
  "prediction_time": "2024-01-15T14:00:00Z",
  "prediction_type": "short",
  "prediction_data": [
    {
      "time": "2024-01-15T14:00:00Z",
      "predicted_power": 245.8,
      "confidence": 94.2
    },
    {
      "time": "2024-01-15T15:00:00Z", 
      "predicted_power": 238.3,
      "confidence": 93.8
    }
    // ... 更多数据点
  ],
  "metadata": {
    "accuracy": 94.2,
    "model_version": "v2.1.0",
    "data_points": 24,
    "updated_at": "2024-01-15T14:05:23Z"
  }
}`}</pre>
                                </div>
                            </Card>

                            <div style={{ marginTop: '16px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '6px', padding: '12px' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>📝 使用说明:</div>
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    <li>请在请求头中添加正确的Authorization token</li>
                                    <li>API支持CORS，允许来源：{selectedApiConfig.outputConfig.allowedOrigins?.join(', ') || '所有来源'}</li>
                                    <li>请求频率限制：{selectedApiConfig.outputConfig.rateLimit}次/小时</li>
                                    <li>数据更新频率：{selectedApiConfig.outputConfig.frequency === 'realtime' ? '实时' : '每小时'}</li>
                                    <li>如遇到401错误，请检查token是否正确</li>
                                    <li>如遇到429错误，说明请求过于频繁，请稍后再试</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </Modal>

                {/* 站点详情查看模态框 */}
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ThunderboltOutlined style={{ color: '#1890ff' }} />
                            站点预测详情
                            {selectedStation && (
                                <Tag color={selectedStation.type === 'wind' ? 'blue' : selectedStation.type === 'solar' ? 'orange' : 'green'}>
                                    {selectedStation.type === 'wind' ? '风电' : selectedStation.type === 'solar' ? '光伏' : '水电'}
                                </Tag>
                            )}
                        </div>
                    }
                    open={isStationModalOpen}
                    onCancel={() => {
                        setIsStationModalOpen(false);
                        setSelectedStation(null);
                    }}
                    footer={[
                        <Button key="export" icon={<DownloadOutlined />}>
                            导出预测数据
                        </Button>,
                        <Button key="close" onClick={() => {
                            setIsStationModalOpen(false);
                            setSelectedStation(null);
                        }}>
                            关闭
                        </Button>
                    ]}
                    width={1000}
                >
                    {selectedStation && (
                        <div>
                            {/* 站点基本信息 */}
                            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                                <Col span={8}>
                                    <Card size="small" style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                                            {selectedStation.type === 'wind' ? '🌪️' : selectedStation.type === 'solar' ? '☀️' : '💧'}
                                        </div>
                                        <Title level={4} style={{ margin: '8px 0' }}>
                                            {selectedStation.name}
                                        </Title>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            <div>📍 {selectedStation.lat.toFixed(2)}°, {selectedStation.lng.toFixed(2)}°</div>
                                            <div>🏠 {currentRegion.name}</div>
                                        </div>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card size="small">
                                        <Statistic
                                            title="预测精度"
                                            value={selectedStation.accuracy}
                                            suffix="%"
                                            valueStyle={{ color: selectedStation.accuracy && selectedStation.accuracy > 90 ? '#52c41a' : '#faad14' }}
                                            prefix={<CheckCircleOutlined />}
                                        />
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                            大模型预测结果
                                        </div>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card size="small">
                                        <Statistic
                                            title="预测速度"
                                            value={selectedStation.predictionTime ? ((Date.now() - selectedStation.predictionTime) / 1000).toFixed(1) : '0.8'}
                                            suffix="秒"
                                            valueStyle={{ color: '#1890ff' }}
                                            prefix={<RobotOutlined />}
                                        />
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                            ⚡ 相比传统方法提速20倍
                                        </div>
                                    </Card>
                                </Col>
                            </Row>

                            {/* 预测结果图表 */}
                            <Card
                                title={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>
                                            {batchPredictionType === 'short' ? '24小时' : '4小时'}功率预测曲线
                                        </span>
                                        <div>
                                            <Tag color="blue">大模型预测</Tag>
                                            <Tag color="green">精度: {selectedStation.accuracy}%</Tag>
                                        </div>
                                    </div>
                                }
                                size="small"
                            >
                                <ResponsiveContainer width="100%" height={400}>
                                    <LineChart data={selectedStation.predictionData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="time" />
                                        <YAxis label={{ value: '功率 (MW)', angle: -90, position: 'insideLeft' }} />
                                        <RechartsTooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="actual"
                                            stroke="#8884d8"
                                            name="实际功率"
                                            strokeWidth={2}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="predicted"
                                            stroke="#52c41a"
                                            name="大模型预测"
                                            strokeWidth={3}
                                            strokeDasharray="5 5"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card>

                            {/* 预测性能对比 */}
                            <Card title="性能对比" size="small" style={{ marginTop: '16px' }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <div style={{ padding: '16px', background: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                <RobotOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                                <Text strong style={{ color: '#52c41a' }}>大模型</Text>
                                            </div>
                                            <div style={{ fontSize: '14px' }}>
                                                <div>⚡ 预测速度: {selectedStation.predictionTime ? ((Date.now() - selectedStation.predictionTime) / 1000).toFixed(1) : '0.8'} 秒</div>
                                                <div>🎯 预测精度: {selectedStation.accuracy}%</div>
                                                <div>🧠 模型类型: 大语言模型</div>
                                                <div>📊 数据处理: 多维度特征提取</div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ padding: '16px', background: '#fff2f0', borderRadius: '6px', border: '1px solid #ffccc7' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                <SettingOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                                                <Text strong style={{ color: '#ff4d4f' }}>传统方法</Text>
                                            </div>
                                            <div style={{ fontSize: '14px' }}>
                                                <div>⏱️ 预测速度: 15-20 秒</div>
                                                <div>📈 预测精度: {Math.max(75, (selectedStation.accuracy || 90) - 8)}%</div>
                                                <div>🔧 模型类型: 统计模型</div>
                                                <div>📋 数据处理: 基础特征分析</div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>

                            {/* 技术优势说明 */}
                            <div style={{ marginTop: '16px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '6px', padding: '12px' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1890ff' }}>
                                    🚀 大模型技术优势:
                                </div>
                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                                    <li><strong>超快速预测:</strong> 采用先进的神经网络架构，预测速度提升20倍</li>
                                    <li><strong>高精度保证:</strong> 多维度特征学习，预测精度提升8-12%</li>
                                    <li><strong>自适应学习:</strong> 能够自动适应不同地区和季节的特征变化</li>
                                    <li><strong>实时优化:</strong> 持续学习最新数据，不断提升预测性能</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </Modal>
            </Card>
        </div>
    );
};

export default PowerPrediction; 